import {
  AppointmentStatus,
  Gender,
  IAddressMetadata,
  ICustomMetadata,
  ISystemPatientMetadata,
  MentionResourceType,
  PatientStatus,
  RecallTrack,
  ReferralSourceType,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  ISerialisedDocumentReference,
  ISerialisedTimestamp,
} from '@principle-theorem/shared';
import { IBigQueryAssociatedTreatment } from './appointment';
import {
  IBigQueryFirestoreModel,
  IBigQueryGeoCoordinates,
  IBigQueryNamedDocument,
  IBigQueryNote,
  IBigQueryReffable,
  IBigQueryStatusHistory,
  ITimestampedDimension,
} from './common';

export type BigQueryReferralSourceType =
  | ReferralSourceType.ConfiguredReferrer
  | MentionResourceType.Patient
  | MentionResourceType.Contact
  | 'unknown';

export interface IBigQueryReferrer
  extends AtLeast<IBigQueryNamedDocument, 'name'> {
  type: BigQueryReferralSourceType;
}

interface IBasePatientDimension extends ITimestampedDimension {
  name: string;
  referrer?: IBigQueryReferrer;
  relationships: IBigQueryPatientRelationship[];
  dateOfBirth?: ISerialisedTimestamp;
  gender: Gender;
  status: PatientStatus;
  statusHistory: IBigQueryStatusHistory<PatientStatus>[];
  preferredDentist?: IBigQueryNamedDocument;
  preferredHygienist?: IBigQueryNamedDocument;
  preferredPractice?: IBigQueryNamedDocument;
  preferredFeeSchedule?: IBigQueryNamedDocument;
  notes: IBigQueryNote[];
  tags: IBigQueryNamedDocument[];
  coordinates?: IBigQueryGeoCoordinates;
  profileImageURL?: string;
  medicareCard?: IBigQueryMedicareCard;
  healthFundCard?: IBigQueryHealthFundCard;
  dvaCard?: IBigQueryDVACard;
  referenceId?: string;
  recall?: IBigQueryPatientRecall;
  secondaryRecall?: IBigQueryPatientRecall;
  combinedRecall?: {
    nextRecallDueAt?: ISerialisedTimestamp;
    lastRecallCompletedAt?: ISerialisedTimestamp;
    hasNextRecallAppointment?: boolean;
    nextRecallSourceTrack?: RecallTrack;
  };
}

interface IBigQueryPatientRelationship {
  patient: IBigQueryNamedDocument;
  /**
   * PatientRelationshipType is an invalid avro enum
   */
  type: string;
}

interface IBigQueryMedicareCard {
  number: string;
  subNumerate: string;
  expiryDate?: ISerialisedTimestamp;
}

interface IBigQueryHealthFundCard {
  membershipNumber: string;
  memberNumber: string;
  fundCode?: string;
}

interface IBigQueryDVACard {
  number: string;
  expiryDate?: ISerialisedTimestamp;
}

interface IBigQueryPatientContactDetails {
  email: string;
  mobileNumber?: string;
  contactNumbers: { label: string; number: string }[];
  address: string;
}

export interface IBigQueryHasPrimaryContact {
  primaryContact: IBigQueryPatientRelationship;
}

export interface IBigQueryIsPrimaryContact {
  isPrimaryContact: boolean;
}

export interface IBigQueryAppointmentSummary extends IBigQueryReffable {
  from: ISerialisedTimestamp;
  to: ISerialisedTimestamp;
  status: AppointmentStatus;
  practice: IBigQueryNamedDocument;
  practitioner: IBigQueryNamedDocument;
  treatmentPlan: IBigQueryAssociatedTreatment;
  tags: IBigQueryNamedDocument[];
}

export interface IBigQueryAccountSummary {
  creditSummary: IBigQueryAccountCreditSummary;
  invoiceSummary: IBigQueryAccountInvoiceSummary;
  paymentSummary: IBigQueryAccountPaymentSummary;
}

export interface IBigQueryAccountCreditSummary {
  freeCredit: number;
  depositsPaid: number;
  creditTotal: number;
  creditUsed: number;
  creditRemaining: number;
}

export interface IBigQueryAccountInvoiceSummary {
  treatments: number;
  products: number;
  fees: number;
  subtotal: number;
  deposits: number;
  totalInvoiced: number;
}

export interface IBigQueryAccountPaymentSummary {
  discounts: number;
  writtenOff: number;
  creditUsed: number;
  totalReceivable: number;
  paymentsReceived: number;
  outstanding: number;
}

interface IBigQueryAddressMetadata
  extends Omit<IAddressMetadata, 'components'> {
  components?: {
    country?: string;
    state?: string;
    suburb?: string;
    postalCode?: string;
  };
}

export interface IBigQueryPatientMetadata
  extends Partial<ICustomMetadata>,
    Omit<Partial<ISystemPatientMetadata>, 'address'> {
  address?: IBigQueryAddressMetadata;
}

export interface IBigQueryPatientRecall {
  periodInMonths: number;
  periodIsExplicit: boolean;
  nextRecallDue: {
    isSet: boolean;
    isExplicit: boolean;
    dueAt?: ISerialisedTimestamp;
  };
  lastCompletedAt?: ISerialisedTimestamp;
  lastRecallAppointment?: ISerialisedDocumentReference;
  nextRecallAppointment?: ISerialisedDocumentReference;
}

export interface IPatientDimension
  extends IBigQueryReffable,
    IBigQueryFirestoreModel,
    IBasePatientDimension,
    Partial<IBigQueryPatientContactDetails>,
    Partial<IBigQueryHasPrimaryContact>,
    Partial<IBigQueryIsPrimaryContact> {
  mobileNumber?: string;
  accountSummary: IBigQueryAccountSummary;
  lastVisitRef?: ISerialisedDocumentReference;
  nextVisitRef?: ISerialisedDocumentReference;
  lastVisit?: IBigQueryAppointmentSummary;
  nextVisit?: IBigQueryAppointmentSummary;
  metadata?: Partial<IBigQueryPatientMetadata>;
}
